
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Volume2, VolumeX, Pause, Play } from 'lucide-react';
import { textToSpeechService } from '@/services/textToSpeechService';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

interface TextToSpeechProps {
  text: string;
}

type SpeechStatus = 'idle' | 'speaking' | 'paused';

const TextToSpeech: React.FC<TextToSpeechProps> = ({ text }) => {
  const [status, setStatus] = useState<SpeechStatus>('idle');
  const [supported, setSupported] = useState<boolean>(false);
  const [voicesLoaded, setVoicesLoaded] = useState<boolean>(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if speech synthesis is supported
    const isSpeechSupported = textToSpeechService.isSupported();
    setSupported(isSpeechSupported);
    
    if (isSpeechSupported) {
      // Set up voice changed event listener to handle async voice loading
      const handleVoicesChanged = () => {
        const voices = textToSpeechService.getVoices();
        if (voices.length > 0) {
          console.log("Voices loaded:", voices.length);
          setVoicesLoaded(true);
        }
      };
      
      // Try loading voices immediately
      const voices = textToSpeechService.getVoices();
      if (voices.length > 0) {
        setVoicesLoaded(true);
      }
      
      // Set up event listener for when voices are loaded
      window.speechSynthesis.onvoiceschanged = handleVoicesChanged;
      
      // Set up speech ended event detection
      const checkSpeechEnded = setInterval(() => {
        if (status === 'speaking' && !window.speechSynthesis.speaking) {
          setStatus('idle');
        }
      }, 100);
      
      // Clean up on unmount
      return () => {
        if (status !== 'idle') {
          textToSpeechService.stop();
        }
        window.speechSynthesis.onvoiceschanged = null;
        clearInterval(checkSpeechEnded);
      };
    }
  }, []);

  useEffect(() => {
    // Reset status when text changes
    if (status !== 'idle') {
      textToSpeechService.stop();
      setStatus('idle');
    }
  }, [text]);

  const toggleSpeech = () => {
    if (status === 'idle') {
      try {
        console.log("Starting speech with text:", text.substring(0, 50) + "...");
        textToSpeechService.speak({ text, lang: language === 'ur' ? 'ur' : 'en-US' });
        setStatus('speaking');
        toast.success(language === 'ur' ? 'بولنا شروع کیا' : 'Started speaking', {
          id: 'speech-toast',
          duration: 2000
        });
      } catch (error) {
        console.error('Speech synthesis error:', error);
        toast.error(language === 'ur' ? 'آواز چلانے میں مسئلہ ہوا' : 'Error in speech synthesis');
      }
    } else if (status === 'speaking') {
      textToSpeechService.pause();
      setStatus('paused');
    } else if (status === 'paused') {
      textToSpeechService.resume();
      setStatus('speaking');
    }
  };

  const stopSpeech = () => {
    textToSpeechService.stop();
    setStatus('idle');
  };

  if (!supported) {
    return null; // Don't show anything if speech synthesis is not supported
  }

  if (!voicesLoaded) {
    return (
      <Button
        disabled
        variant="outline"
        size="sm"
        className="flex items-center gap-1"
      >
        <Volume2 size={16} />
        {language === 'ur' ? 'آوازیں لوڈ ہو رہی ہیں...' : 'Loading voices...'}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {status === 'idle' ? (
        <Button
          onClick={toggleSpeech}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
          title={language === 'ur' ? 'پڑھ کر سنائیں' : 'Listen'}
        >
          <Volume2 size={16} />
          {language === 'ur' ? 'سنیں' : 'Listen'}
        </Button>
      ) : (
        <>
          <Button
            onClick={toggleSpeech}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
          >
            {status === 'speaking' ? <Pause size={16} /> : <Play size={16} />}
            {status === 'speaking' 
              ? (language === 'ur' ? 'روکیں' : 'Pause')
              : (language === 'ur' ? 'جاری رکھیں' : 'Resume')}
          </Button>
          <Button
            onClick={stopSpeech}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 text-red-500"
          >
            <VolumeX size={16} />
            {language === 'ur' ? 'بند کریں' : 'Stop'}
          </Button>
        </>
      )}
    </div>
  );
};

export default TextToSpeech;
